import { useMemo, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import messages from "../../../../../../assets/locale/messages";
import { exportCompanyPortalEventRequests } from "../../../../../../network/apis/companyPortalEventRequests";
import { exportAdminEventRequests } from "../../../../../../network/apis/applicantTabExportSheet";
import {
  localizationKey,
  localizedCheckInStatus,
  localizedGender,
  showErrorMsg,
  showSuccessMsg,
} from "../../../../../../utils/Helpers";
import {
  APPLICANT_EXPORT_NATIONAL_ID_COLUMN,
  extractApplicantExportRows,
  getApplicantExportCellValue,
  normalizeApplicantExportRows,
} from "../../../../../../utils/applicantExportNormalize";
import { embedApplicantProfileImages } from "../../../../../../utils/excelExportImages";

// No portal's export sheet has a Date column.
const exportFieldDefaults = {
  applicantID: false,
  imageUrl: false,
  name: false,
  gender: false,
  zone: false,
  checkInStatus: false,
  status: false,
  role: false,
  note: false,
  stcNumber: false,
  nationalId: false,
  autoDeductionAmount: false,
  manualDeductionAmount: false,
  manualDeductionReason: false,
  rewardAmount: false,
  rewardReason: false,
  dailyAmount: false,
  totalAmount: false,
  numberOfWorking: false,
  trainingDays: false,
  trainingDailyAmount: false,
  trainingTotalAmount: false,
  totalShiftAmount: false,
};

const companyExportColumns = {
  applicantID: "ID",
  imageUrl: "Image URL",
  name: "Name",
  gender: "Gender",
  zone: "Zone",
  checkInStatus: "Check-in Status",
  status: "Allocation Status",
  role: "Role",
  note: "Note",
  stcNumber: "Stc Number",
  nationalId: "National Id",
  autoDeductionAmount: "Auto Deduction Amount",
  manualDeductionAmount: "Manual Deduction Amount",
  manualDeductionReason: "Manual Deduction Reason",
  rewardAmount: "Reward Amount",
  rewardReason: "Reward Reason",
  dailyAmount: "Daily Amount",
  totalAmount: "Total Amount",
  numberOfWorking: "Number of Working Days",
  trainingDays: "Number of Training Days",
  trainingDailyAmount: "Daily Training Amount",
  trainingTotalAmount: "Total Training Amount",
  totalShiftAmount: "Total Shift Amount",
};

const getExportDateValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.split("T")[0];
  if (value instanceof Date) return value.toISOString().split("T")[0];
  if (typeof value.format === "function") return value.format("YYYY-MM-DD");
  return "";
};

const sanitizeFileName = (name, fallback) =>
  String(name || fallback)
    .trim()
    .replace(/[^\w-]+/g, "-")
    .replace(/^-+|-+$/g, "") || fallback;

const EXPORT_EMPTY_VALUES = new Set(["", "-", "–", "—", "--", "n/a", "na", "null"]);

/**
 * Allocation Status is derived client-side from the applicant's zone (mirroring the
 * applicants table) — the export endpoint returns a wrong, always-"Assigned" value.
 */
const isApplicantAssignedToZone = (row = {}) => {
  if (row?.zone_id != null && row?.zone_id !== "") return true;
  if (row?.zone && typeof row.zone === "object") return Boolean(row.zone?.id || row.zone?.name);

  const raw =
    row?.[companyExportColumns.zone] ?? row?.zone_name ?? row?.zone ?? "";
  return !EXPORT_EMPTY_VALUES.has(String(raw).trim().toLowerCase());
};

/** Export endpoint expects enum strings for filter params (list API uses booleans). */
const normalizeAdminExportBody = (body) => {
  const next = { ...body };
  if (next.is_checked_in === true) {
    next.is_checked_in = "checked_in";
  } else if (next.is_checked_in === false) {
    next.is_checked_in = "not_checked_in";
  }
  if (next.is_assigned_to_zone === true) {
    next.is_assigned_to_zone = "assigned";
  } else if (next.is_assigned_to_zone === false) {
    next.is_assigned_to_zone = "not_assigned";
  }
  return next;
};

/** Localizes the .xlsx header labels and the free-text cell values for Arabic exports. */
const buildExportLocalizers = (lang) => {
  const isAr = lang === "ar";
  const exportCopy = messages[lang]?.events?.exportApplicants || {};

  const localizeHeader = (header) => {
    if (!isAr) return header;
    if (header === companyExportColumns.status) {
      return exportCopy.allocationStatus || header;
    }
    return exportCopy[localizationKey(header)] || header;
  };

  const localizeCellValue = (header, value) => {
    if (!isAr) return value;
    if (header === companyExportColumns.gender) return localizedGender(value) || value;
    if (header === companyExportColumns.checkInStatus) {
      return localizedCheckInStatus(value) || value;
    }
    return value;
  };

  return { localizeHeader, localizeCellValue };
};

const downloadXlsx = async (filename, rows, lang, selectedColumns) => {
  const { localizeHeader, localizeCellValue } = buildExportLocalizers(lang);
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("joined_applicants", {
    views: [{ rightToLeft: lang === "ar" }],
  });
  const responseHeaders = [
    ...new Set((rows ?? []).flatMap((row) => Object.keys(row || {}))),
  ];
  const preferredHeaderOrder = Object.values(companyExportColumns);
  const visibleHeaders = new Set(selectedColumns);
  const headers = [
    ...preferredHeaderOrder.filter((header) => visibleHeaders.has(header)),
    ...responseHeaders.filter(
      (header) =>
        visibleHeaders.has(header) && !preferredHeaderOrder.includes(header),
    ),
  ];

  worksheet.columns = headers.map((header) => {
    const label = localizeHeader(header);
    return {
      header: label,
      key: header,
      width: Math.min(Math.max(String(label).length + 8, 18), 45),
    };
  });
  worksheet.getRow(1).font = { bold: true };
  worksheet.getRow(1).alignment = { vertical: "middle", horizontal: "center" };
  worksheet.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "D9E1F2" },
  };
  rows.forEach((row) => {
    const addedRow = worksheet.addRow(
      headers.map((header) =>
        getApplicantExportCellValue(header, localizeCellValue(header, row[header])),
      ),
    );

    const nationalIdColIndex = headers.indexOf(APPLICANT_EXPORT_NATIONAL_ID_COLUMN);
    if (nationalIdColIndex >= 0) {
      const cell = addedRow.getCell(nationalIdColIndex + 1);
      if (cell.value != null && cell.value !== "" && cell.value !== "-") {
        cell.value = String(cell.value);
        cell.numFmt = "@";
      }
    }
  });

  await embedApplicantProfileImages({
    workbook,
    worksheet,
    rows: rows ?? [],
    headers,
    dataStartRowNumber: 2,
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${filename}.xlsx`);
};

const useApplicantsExport = ({
  event,
  lang,
  listParams,
  selectedIds,
  portalVariant = "company-portal",
}) => {
  const isAdminPortal = portalVariant === "admin-portal";
  const { events, shared } = messages[lang];
  const applicantsTab = events.viewEvents.applicantsTab;
  const allocationStatusLabels =
    applicantsTab.joiningRequests?.allocationStatusFilter?.options || {};
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportMode, setExportMode] = useState("all");
  const [isExporting, setIsExporting] = useState(false);
  const [selectedExportFields, setSelectedExportFields] = useState(
    exportFieldDefaults,
  );
  const [exportDate, setExportDate] = useState(null);
  const [dateError, setDateError] = useState(false);

  const fields = useMemo(
    () => [
      { name: "applicantID", label: shared.id },
      { name: "imageUrl", label: shared.lists.image },
      { name: "name", label: shared.lists.name },
      { name: "gender", label: shared.gender },
      { name: "zone", label: applicantsTab.zone },
      { name: "checkInStatus", label: events.viewEvents.checkinStatus },
      { name: "status", label: events.exportApplicants.allocationStatus },
      { name: "role", label: applicantsTab.role },
      { name: "note", label: shared.note },
      { name: "stcNumber", label: shared.stcNumber },
      { name: "nationalId", label: events.exportApplicants.nationalId },
      {
        name: "autoDeductionAmount",
        label: events.exportApplicants.autoDeductionAmount,
      },
      {
        name: "manualDeductionAmount",
        label: events.exportApplicants.manualDeductionAmount,
      },
      {
        name: "manualDeductionReason",
        label: events.exportApplicants.manualDeductionReason,
      },
      { name: "rewardAmount", label: events.exportApplicants.rewardAmount },
      { name: "rewardReason", label: events.exportApplicants.rewardReason },
      { name: "dailyAmount", label: shared.dailyAmount },
      { name: "totalAmount", label: shared.totalAmount },
      { name: "numberOfWorking", label: shared.numberOfWorkingDays },
      { name: "trainingDays", label: applicantsTab.trainingDays },
      { name: "trainingDailyAmount", label: applicantsTab.trainingDailyAmount },
      { name: "trainingTotalAmount", label: applicantsTab.trainingTotalAmount },
      { name: "totalShiftAmount", label: applicantsTab.totalShiftAmount },
    ],
    [applicantsTab, events, shared],
  );

  const isAllSelected = Object.values(selectedExportFields).every(Boolean);
  const hasSelectedField = Object.values(selectedExportFields).some(Boolean);
  const isConfirmDisabled =
    isExporting || !hasSelectedField || (selectedExportFields.date && !exportDate);

  const closeModal = () => {
    setIsExportModalOpen(false);
    setExportMode("all");
    setSelectedExportFields(exportFieldDefaults);
    setExportDate(null);
    setDateError(false);
  };

  const openModal = (mode) => {
    setExportMode(mode);
    setIsExportModalOpen(true);
  };

  const handleFieldChange = (event) => {
    const { name, checked } = event.target;
    setSelectedExportFields((current) => ({ ...current, [name]: checked }));
    if (name === "date" && !checked) {
      setExportDate(null);
      setDateError(false);
    }
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectedExportFields(
      Object.fromEntries(
        Object.keys(exportFieldDefaults).map((key) => [key, checked]),
      ),
    );
    setExportDate(checked ? new Date().toISOString() : null);
    setDateError(false);
  };

  const handleDateChange = (date) => {
    setExportDate(date);
    setDateError(!date);
  };

  const getSelectedColumns = () =>
    Object.entries(companyExportColumns)
      .filter(([fieldName]) => selectedExportFields[fieldName])
      .map(([, column]) => column);

  const submit = (eventSubmit) => {
    eventSubmit?.preventDefault?.();

    if (selectedExportFields.date && !exportDate) {
      setDateError(true);
      return;
    }

    const selectedColumns = getSelectedColumns();
    const isAllocationStatusSelected = Boolean(selectedExportFields.status);
    const requestColumns = Array.from(
      new Set([
        "ID",
        ...selectedColumns.filter(
          (column) => column !== companyExportColumns.status,
        ),
        ...(isAllocationStatusSelected ? [companyExportColumns.zone] : []),
      ]),
    );
    const requestBody = {
      locale: lang,
      ...Object.fromEntries(
        Object.entries(listParams).filter(
          ([key]) => !["page", "items"].includes(key),
        ),
      ),
      columns: requestColumns.join(","),
    };
    const dateValue = getExportDateValue(exportDate);

    if (dateValue) {
      requestBody.date = dateValue;
    }

    if (exportMode === "selected") {
      requestBody.event_request_ids = selectedIds.map((id) => Number(id));
    }

    const exportBody = isAdminPortal
      ? normalizeAdminExportBody(requestBody)
      : requestBody;

    setIsExporting(true);

    const exportRequest = isAdminPortal
      ? exportAdminEventRequests(exportBody, { handlerEnabled: false })
      : exportCompanyPortalEventRequests(exportBody, { handlerEnabled: false });

    exportRequest
      .then(async (response) => {
        const rows = normalizeApplicantExportRows(
          extractApplicantExportRows(response),
          selectedColumns,
        );
        const normalizedRows = isAllocationStatusSelected
          ? rows.map((row) => ({
              ...row,
              [companyExportColumns.status]: isApplicantAssignedToZone(row)
                ? allocationStatusLabels.assigned || applicantsTab.assigned
                : allocationStatusLabels.notAssigned || applicantsTab.notAssigned,
            }))
          : rows;
        const fileBase = sanitizeFileName(event?.name, "joined-applicants");
        await downloadXlsx(
          `${fileBase}-joined-applicants`,
          normalizedRows,
          lang,
          selectedColumns,
        );
        showSuccessMsg("File downloaded successfully.", true);
        closeModal();
      })
      .catch((error) => {
        showErrorMsg(error.response?.data?.message || "Error while Exporting File");
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  return {
    dateError,
    exportDate,
    exportMode,
    fields,
    isAllSelected,
    isConfirmDisabled,
    isExporting,
    isOpen: isExportModalOpen,
    selectedFields: selectedExportFields,
    closeModal,
    copy: events.exportApplicants,
    handleDateChange,
    handleFieldChange,
    handleSelectAll,
    openModal,
    submit,
  };
};

export default useApplicantsExport;
